import { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps'
import api from '../api/client'
import type { ClubWithDistance } from '../types/club'
import { telHref } from '../lib/phone'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

/** A colored circular dot marker as an SVG data URI (works without a Map ID). */
function dotIcon(color: string, r: number): string {
  const size = (r + 2) * 2
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">` +
    `<circle cx="${size / 2}" cy="${size / 2}" r="${r}" fill="${color}" stroke="#ffffff" stroke-width="2"/></svg>`
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg)
}

/** The secretary's phone, dialable on a phone, plain text when undialable. */
function PhoneLine({ phone, className }: { phone: string; className: string }) {
  const href = telHref(phone)
  if (href === '') {
    return <p className={className}>{phone}</p>
  }
  return (
    <p className={className}>
      <a href={href} className="hover:text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
        {phone}
      </a>
    </p>
  )
}

const RADIUS_OPTIONS = [5, 10, 25, 50, 100] as const
const DEFAULT_RADIUS = 25
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }
const DEFAULT_ZOOM = 4

interface SearchLocation {
  lat: number
  lng: number
  label: string
}

export function FindClub() {
  return (
    <>
      <Helmet>
        <title>Find a Club - ARPU</title>
        <meta name="description" content="Find an American Racing Pigeon Union club near you." />
      </Helmet>

      <div>
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Find a Club</h1>
        <p className="text-text-light mb-6">
          Search for an AU club near you by entering your address or ZIP code.
        </p>

        {GOOGLE_MAPS_API_KEY ? (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <FindClubInner />
          </APIProvider>
        ) : (
          <FindClubNoMap />
        )}
      </div>
    </>
  )
}

/** Main content rendered inside APIProvider so google.maps is available */
function FindClubInner() {
  const [address, setAddress] = useState('')
  const [radius, setRadius] = useState<number>(DEFAULT_RADIUS)
  const [location, setLocation] = useState<SearchLocation | null>(null)
  const [clubs, setClubs] = useState<ClubWithDistance[]>([])
  const [loading, setLoading] = useState(false)
  const [geolocating, setGeolocating] = useState(false)
  const [error, setError] = useState('')
  const [selectedClub, setSelectedClub] = useState<ClubWithDistance | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const geocodingLib = useMapsLibrary('geocoding')

  const searchClubs = useCallback(async (lat: number, lng: number, r: number) => {
    setLoading(true)
    setError('')
    setHasSearched(true)
    try {
      const { data } = await api.get<{ clubs: ClubWithDistance[] }>('/clubs/find', {
        params: { lat, lng, radius: r },
      })
      setClubs(data.clubs)
      if (data.clubs.length === 0) {
        setError('No clubs found within this radius. Try increasing the search distance.')
      }
    } catch {
      setError('Failed to search for clubs. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  async function handleAddressSearch(e: { preventDefault: () => void }) {
    e.preventDefault()
    if (!address.trim() || !geocodingLib) return

    setLoading(true)
    setError('')
    try {
      const geocoder = new geocodingLib.Geocoder()
      const result = await geocoder.geocode({ address: address.trim() })
      if (result.results.length === 0) {
        setError('Could not find that location. Try a different address or ZIP code.')
        setLoading(false)
        return
      }
      const pos = result.results[0].geometry.location
      const loc = { lat: pos.lat(), lng: pos.lng(), label: result.results[0].formatted_address }
      setLocation(loc)
      await searchClubs(loc.lat, loc.lng, radius)
    } catch {
      setError('Could not geocode that address. Try entering a ZIP code instead.')
      setLoading(false)
    }
  }

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.')
      return
    }
    setGeolocating(true)
    setError('')
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: 'Your location',
        }
        setLocation(loc)
        setAddress('')
        setGeolocating(false)
        await searchClubs(loc.lat, loc.lng, radius)
      },
      () => {
        setError('Unable to get your location. Please allow location access or enter an address.')
        setGeolocating(false)
      }
    )
  }

  async function handleRadiusChange(newRadius: number) {
    setRadius(newRadius)
    if (location) {
      await searchClubs(location.lat, location.lng, newRadius)
    }
  }

  return (
    <>
      <SearchForm
        address={address}
        setAddress={setAddress}
        radius={radius}
        loading={loading}
        geolocating={geolocating}
        onSearch={handleAddressSearch}
        onUseMyLocation={handleUseMyLocation}
        onRadiusChange={handleRadiusChange}
      />

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="order-2 lg:order-1 w-full lg:w-1/2 h-[280px] sm:h-[360px] lg:h-[600px] rounded-lg overflow-hidden border border-border">
          <Map
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            className="w-full h-full"
          >
            <MapContent
              location={location}
              clubs={clubs}
              selectedClub={selectedClub}
              onSelectClub={setSelectedClub}
            />
          </Map>
        </div>

        <div className="order-1 lg:order-2 w-full lg:w-1/2">
          <ClubList
            clubs={clubs}
            loading={loading}
            hasSearched={hasSearched}
            selectedClub={selectedClub}
            onSelectClub={setSelectedClub}
          />
        </div>
      </div>
    </>
  )
}

/** Fallback when no Google Maps API key is set */
function FindClubNoMap() {
  const [address, setAddress] = useState('')
  const [radius, setRadius] = useState<number>(DEFAULT_RADIUS)
  const [clubs, setClubs] = useState<ClubWithDistance[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  async function searchClubs(lat: number, lng: number, r: number) {
    setLoading(true)
    setError('')
    setHasSearched(true)
    try {
      const { data } = await api.get<{ clubs: ClubWithDistance[] }>('/clubs/find', {
        params: { lat, lng, radius: r },
      })
      setClubs(data.clubs)
      if (data.clubs.length === 0) {
        setError('No clubs found within this radius. Try increasing the search distance.')
      }
    } catch {
      setError('Failed to search for clubs. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  function handleUseMyLocation() {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.')
      return
    }
    setError('')
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setAddress('')
        await searchClubs(position.coords.latitude, position.coords.longitude, radius)
      },
      () => {
        setError('Unable to get your location. Please allow location access.')
      }
    )
  }

  return (
    <>
      <div className="bg-warning/10 border border-warning/30 text-text rounded-md px-4 py-3 mb-6 text-sm">
        Google Maps is not configured. You can still search by using your location.
      </div>

      <SearchForm
        address={address}
        setAddress={setAddress}
        radius={radius}
        loading={loading}
        geolocating={false}
        onSearch={(e) => { e.preventDefault() }}
        onUseMyLocation={handleUseMyLocation}
        onRadiusChange={(r) => setRadius(r)}
        addressDisabled
      />

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {hasSearched && (
        <div className="space-y-3 mt-4">
          <h2 className="text-lg font-heading font-semibold text-text">
            {clubs.length} club{clubs.length !== 1 ? 's' : ''} found
          </h2>
          {clubs.map((club) => (
            <ClubCard key={club.clubNum} club={club} isSelected={false} onSelect={() => {}} />
          ))}
        </div>
      )}
    </>
  )
}

function SearchForm({
  address,
  setAddress,
  radius,
  loading,
  geolocating,
  onSearch,
  onUseMyLocation,
  onRadiusChange,
  addressDisabled,
}: {
  address: string
  setAddress: (v: string) => void
  radius: number
  loading: boolean
  geolocating: boolean
  onSearch: (e: { preventDefault: () => void }) => void
  onUseMyLocation: () => void
  onRadiusChange: (r: number) => void
  addressDisabled?: boolean
}) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 mb-6">
      <form onSubmit={onSearch} className="flex flex-col md:flex-row gap-3">
        <div className="flex-1">
          <label htmlFor="address" className="sr-only">Address or ZIP code</label>
          <input
            id="address"
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Enter address, city, or ZIP code"
            disabled={addressDisabled}
            className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:opacity-50"
          />
        </div>
        {!addressDisabled && (
          <button
            type="submit"
            disabled={loading || !address.trim()}
            className="px-5 py-2 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        )}
        <button
          type="button"
          onClick={onUseMyLocation}
          disabled={geolocating || loading}
          className="px-4 py-2 border border-border hover:border-primary/30 bg-surface hover:bg-surface-hover text-text-light font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {geolocating ? 'Locating...' : 'Use my location'}
        </button>
      </form>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-text-muted">Radius:</span>
        {RADIUS_OPTIONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onRadiusChange(r)}
            className={`px-3 py-1 text-sm rounded-full border transition-colors ${
              radius === r
                ? 'bg-primary text-text-inverse border-primary'
                : 'bg-surface text-text-light border-border hover:border-primary/30'
            }`}
          >
            {r} mi
          </button>
        ))}
      </div>
    </div>
  )
}

/** Handles map markers and auto-fitting bounds when results change */
function MapContent({
  location,
  clubs,
  selectedClub,
  onSelectClub,
}: {
  location: SearchLocation | null
  clubs: ClubWithDistance[]
  selectedClub: ClubWithDistance | null
  onSelectClub: (club: ClubWithDistance | null) => void
}) {
  const map = useMap()
  const coreLib = useMapsLibrary('core')
  const boundsSet = useRef(false)

  useEffect(() => {
    if (!map || !coreLib) return
    if (clubs.length > 0 && location) {
      const bounds = new coreLib.LatLngBounds()
      bounds.extend({ lat: location.lat, lng: location.lng })
      clubs.forEach((c) => bounds.extend({ lat: c.lat, lng: c.lng }))
      map.fitBounds(bounds, 50)
      boundsSet.current = true
    } else if (location && !boundsSet.current) {
      map.panTo({ lat: location.lat, lng: location.lng })
      map.setZoom(10)
    }
  }, [map, coreLib, clubs, location])

  return (
    <>
      {location && (
        <Marker position={{ lat: location.lat, lng: location.lng }} icon={dotIcon('#001578', 9)} />
      )}

      {clubs.map((club) => (
        <Marker
          key={club.clubNum}
          position={{ lat: club.lat, lng: club.lng }}
          onClick={() => onSelectClub(club)}
          icon={dotIcon('#d11149', 7)}
        />
      ))}

      {selectedClub && (
        <InfoWindow
          position={{ lat: selectedClub.lat, lng: selectedClub.lng }}
          onCloseClick={() => onSelectClub(null)}
          pixelOffset={[0, -10]}
        >
          <div className="p-1 max-w-[240px]">
            <h3 className="font-semibold text-sm mb-1">{selectedClub.clubName}</h3>
            <p className="text-xs text-gray-600 mb-1">{selectedClub.distance} miles away</p>
            {selectedClub.secretaryName && (
              <p className="text-xs text-gray-600">Secretary: {selectedClub.secretaryName}</p>
            )}
            {selectedClub.secretaryPhone && (
              <PhoneLine phone={selectedClub.secretaryPhone} className="text-xs text-gray-600" />
            )}
            {selectedClub.secretaryEmail && (
              <p className="text-xs text-gray-600 break-all">
                <a href={`mailto:${selectedClub.secretaryEmail}`} className="hover:underline">
                  {selectedClub.secretaryEmail}
                </a>
              </p>
            )}
            <Link
              to={`/clubs/${selectedClub.slug}`}
              className="inline-block mt-2 text-xs text-blue-600 hover:underline"
            >
              View club details
            </Link>
          </div>
        </InfoWindow>
      )}
    </>
  )
}

function ClubList({
  clubs,
  loading,
  hasSearched,
  selectedClub,
  onSelectClub,
}: {
  clubs: ClubWithDistance[]
  loading: boolean
  hasSearched: boolean
  selectedClub: ClubWithDistance | null
  onSelectClub: (club: ClubWithDistance | null) => void
}) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (selectedClub && listRef.current) {
      const el = listRef.current.querySelector(`[data-club="${selectedClub.clubNum}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [selectedClub])

  if (loading) {
    return (
      <div className="text-center py-12 text-text-muted">
        Searching for nearby clubs...
      </div>
    )
  }

  if (!hasSearched) {
    return (
      <div className="text-center py-12 text-text-muted">
        Enter an address or use your location to find clubs near you.
      </div>
    )
  }

  if (clubs.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted">
        No clubs found. Try increasing the search radius.
      </div>
    )
  }

  return (
    <div ref={listRef}>
      <h2 className="text-lg font-heading font-semibold text-text mb-3">
        {clubs.length} club{clubs.length !== 1 ? 's' : ''} found
      </h2>
      <div className="space-y-3 lg:max-h-[540px] lg:overflow-y-auto lg:pr-1">
        {clubs.map((club) => (
          <ClubCard
            key={club.clubNum}
            club={club}
            isSelected={selectedClub?.clubNum === club.clubNum}
            onSelect={() => onSelectClub(club)}
          />
        ))}
      </div>
    </div>
  )
}

function ClubCard({
  club,
  isSelected,
  onSelect,
}: {
  club: ClubWithDistance
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <div
      data-club={club.clubNum}
      onClick={onSelect}
      className={`
        p-4 rounded-lg border cursor-pointer transition-all
        ${isSelected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-surface hover:border-primary/30 hover:shadow-sm'
        }
      `}
    >
      <div className="flex justify-between items-start mb-1">
        <h3 className="font-heading font-semibold text-text">{club.clubName}</h3>
        <span className="text-sm text-text-muted whitespace-nowrap ml-3">
          {club.distance} mi
        </span>
      </div>
      {club.clubState && (
        <p className="text-sm text-text-light mb-1">
          {club.clubState} &middot; Zone {club.clubZone}
        </p>
      )}
      {club.secretaryName && (
        <p className="text-sm text-text-muted">
          Secretary: {club.secretaryName}
        </p>
      )}
      {club.secretaryPhone && (
        <PhoneLine phone={club.secretaryPhone} className="text-sm text-text-muted" />
      )}
      {club.secretaryEmail && (
        <p className="text-sm text-text-muted">
          <a href={`mailto:${club.secretaryEmail}`} className="hover:text-primary" onClick={(e) => e.stopPropagation()}>
            {club.secretaryEmail}
          </a>
        </p>
      )}
      <Link
        to={`/clubs/${club.slug}`}
        className="inline-block mt-2 text-sm text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        View club details
      </Link>
    </div>
  )
}
